import React, { PropsWithChildren } from 'react'
import { Dimensions, View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import { ThemeColors } from '@lyrahealth-inc/shared-app-logic/src/features/common/constants/theme/colors'

import { XIcon } from '../../atoms/icons/XIcon'
import { PressableOpacity } from '../../atoms/pressableOpacity/PressableOpacity'
import { TertiaryIconButton } from '../../atoms/tertiaryIconButton/TertiaryIconButton'
import { getFocusBoxShadow } from '../../styles/commonStyles'
import { ThemeType } from '../../utils/themes/ThemeProvider'
import { tID } from '../../utils/utils'
import { ExpandingSidePanel } from '../expandingSidePanel/ExpandingSidePanel.web'

export const getTabButtonPressableProps = ({
  tabId,
  selectedTab,
  hoveredTabId,
  setHoveredTabId,
  colors,
}: {
  tabId: string
  selectedTab: string
  hoveredTabId: string | null
  setHoveredTabId: (tabId: string | null) => void
  colors: ThemeColors
}) => {
  const isActive = selectedTab === tabId
  return {
    getHoverVisible: (hoverVisible: boolean) => {
      if (hoverVisible) {
        setHoveredTabId(tabId)
      } else if (hoveredTabId === tabId) {
        setHoveredTabId(null)
      }
    },
    hoveredBackgroundColor: !isActive ? colors.tertiaryButtonBackgroundPressed : undefined,
    pressedBackgroundColor: colors.tertiaryButtonBackgroundPressed,
    styleByState: (pressed: boolean) => {
      const style: any = {}
      if (pressed && !isActive) {
        style.boxShadow = getFocusBoxShadow({ colors, outerBoxShadowSize: '6px' }).boxShadow
      }
      return style
    },
  }
}

const RIGHT_SIDE_PANEL_WIDTH = 320
const HEADER_HEIGHT = 114

export type TabbedExpandingSidePanelTabItem = {
  id: string
  icon: (isActive: boolean, isHovered?: boolean) => React.ReactNode
  content: React.ReactNode
}

export type TabbedExpandingSidePanelProps = PropsWithChildren<{
  isOpen: boolean
  onClose: () => void
  selectedTab: string
  setSelectedTab: (tab: string) => void
  tabs: TabbedExpandingSidePanelTabItem[]
  transitionTime: number
  contentHeightOffset?: number
  hoveredTabId: string | null
  setHoveredTabId: (tabId: string | null) => void
}>

const Header = styled.View<{ theme: ThemeType }>(({ theme: { spacing, colors } }) => ({
  paddingLeft: spacing['24px'],
  paddingRight: spacing['24px'],
  height: `${HEADER_HEIGHT}px`,
  borderBottomWidth: '1px',
  borderBottomColor: colors.borderDefault,
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  position: 'relative',
  zIndex: 11,
}))

const Content = styled.View<{ screenHeight: number }>(({ screenHeight }) => ({
  height: `${screenHeight - HEADER_HEIGHT}px`,
}))

const NavigationContainer = styled.View({
  flex: 1,
})

const TabsRow = styled.View<{ theme: ThemeType }>(({ theme: { spacing } }) => ({
  flexDirection: 'row',
  alignItems: 'center',
  gap: spacing['8px'],
}))

const TabButton = styled(PressableOpacity)<{ theme: ThemeType; isActive: boolean }>(({ theme: { spacing } }) => ({
  paddingVertical: spacing['8px'],
  paddingHorizontal: spacing['8px'],
  width: '40px',
  height: '40px',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  overflow: 'hidden',
}))

export const TabbedExpandingSidePanel: React.FC<TabbedExpandingSidePanelProps> = ({
  isOpen,
  onClose,
  children,
  selectedTab,
  setSelectedTab,
  tabs,
  transitionTime,
  contentHeightOffset = 0,
  hoveredTabId,
  setHoveredTabId,
}) => {
  const { colors } = useTheme()
  const { height: screenHeight } = Dimensions.get('window')
  const selectedContent = tabs.find((i) => i.id === selectedTab)
  const renderedContent = selectedContent?.content ?? children

  return (
    <ExpandingSidePanel
      isPanelOpen={isOpen}
      panelWidth={RIGHT_SIDE_PANEL_WIDTH}
      panelContainerStyle={{
        padding: '0px',
        borderTopLeftRadius: '0px',
        borderBottomLeftRadius: '0px',
        boxShadow: 'none',
        borderLeftWidth: isOpen ? '1px' : '0px',
        borderLeftStyle: 'solid',
        borderLeftColor: `${colors.borderDefault}`,
      }}
      showsTab={false}
      transitionTime={transitionTime}
    >
      <View>
        <Header>
          <NavigationContainer>
            {/* only show tabs if there are multiple */}
            {tabs.length > 1 && (
              <TabsRow>
                {tabs.map((tab) => {
                  const isActive = selectedTab === tab.id
                  const isHovered = hoveredTabId === tab.id
                  return (
                    <TabButton
                      key={tab.id}
                      isActive={isActive}
                      onPress={() => setSelectedTab(tab.id)}
                      testID={tID(`TabbedExpandingSidePanel-tab-${tab.id}`)}
                      accessibilityRole='tab'
                      accessibilityState={{ selected: isActive }}
                      {...getTabButtonPressableProps({
                        tabId: tab.id,
                        selectedTab,
                        hoveredTabId,
                        setHoveredTabId,
                        colors,
                      })}
                    >
                      {tab.icon ? tab.icon(isActive, isHovered) : null}
                    </TabButton>
                  )
                })}
              </TabsRow>
            )}
          </NavigationContainer>
          <View>
            <TertiaryIconButton
              testID={tID('TabbedExpandingSidePanel-close')}
              leftIcon={<XIcon fillColor={colors.iconDefault} size={24} />}
              onPress={() => onClose()}
            />
          </View>
        </Header>
        <Content screenHeight={screenHeight - contentHeightOffset} testID={tID(`tab-${selectedContent?.id}-content`)}>
          {renderedContent}
        </Content>
      </View>
    </ExpandingSidePanel>
  )
}

export default TabbedExpandingSidePanel
